import React, { useState, useEffect, useCallback } from 'react';
import { propOr, path, isEmpty } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import Loader from 'react-loader-spinner';
import {
  AppProvider,
  Button,
  Card,
  Caption,
  ButtonGroup,
  DropZone,
  Form,
  FormLayout,
  Frame,
  Heading,
  Badge,
  Layout,
  Link,
  Modal,
  Page,
  ResourceItem,
  ResourceList,
  Subheading,
  Stack,
  TextField,
  TextStyle,
  TextContainer,
  Toast,
  RangeSlider,
  Select,
  Tooltip,
  Thumbnail,
} from '@shopify/polaris';
import SuccessBanner from '../components/SuccessBanner';
import { GET_CAREER_PORTAL, GET_CURRENT_SUBSCRIPTION } from '../apollo/queries';
import {
  CREATE_SHOP_CAREER_PORTAL,
  UPDATE_SHOP_CAREER_PORTAL,
  CREATE_TESTIMONIAL,
  UPDATE_TESTIMONIAL,
  DELETE_TESTIMONIAL,
} from '../apollo/mutation';
import { FONT_WEIGHT_OPTIONS, FONT_FAMILY_OPTIONS, FONT_CASING_OPTIONS } from '../../utils/formDefaultValues';

import ColorPicker from '../components/ColorPicker';

import { defaultWhiteColor, defaultDarkGrayColor, hslaColor } from '../../utils/defaultColors';
import { uploadImageUrl } from '../../utils/imagesUrl';
import { keysToCamel } from '../../utils/normalizer';

const EmployeeTestimonialsSection = ({ history, shopifyToken, shopifyDomain, careerPageLink }) => {
  const { push: changeRoute } = history;
  const queryAttr = { variables: { shopifyToken } };

  const [createShopCareerPortal] = useMutation(CREATE_SHOP_CAREER_PORTAL);
  const [updateShopCareerPortal] = useMutation(UPDATE_SHOP_CAREER_PORTAL);
  const [createTestimonial] = useMutation(CREATE_TESTIMONIAL);
  const [updateTestimonial] = useMutation(UPDATE_TESTIMONIAL);
  const [deleteTestimonial] = useMutation(DELETE_TESTIMONIAL);

  const { loading, data, refetch } = useQuery(GET_CAREER_PORTAL, queryAttr);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, queryAttr);

  const shop = propOr({}, 'shop')(subscriptionData);
  const careerPortalSet = propOr(false, 'careerPortalSet')(shop);
  const isCareerPortalEmpty = !careerPortalSet;

  const [form, setForm] = useState({
    testimonials: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessBanner, setShowSuccessBanner] = useState(false);
  const [isSaveBarActive, setIsSaveBarActive] = useState(false);
  const [confirmModal, setConfirmModal] = useState(false);

  const [toastStatus, setToast] = useState({ isOpen: false, text: '' });
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [isTestimonialModalOpen, setIsTestimonialModalOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [file, setFile] = useState();
  const [preview, setPreview] = useState(false);
  const [careerPortalTime, setCareerPortalTime] = useState();

  const toggleIsTestimonialModalOpen = () => setIsTestimonialModalOpen(!isTestimonialModalOpen);
  const toggleIsConfirmDeleteModalOpen = () => setIsConfirmDeleteModalOpen(!isConfirmDeleteModalOpen);
  const togglePreviewModal = () => setPreview(!preview);
  const toggleConfirmModal = () => setConfirmModal(!confirmModal);

  const handleDropZoneDrop = useCallback((_dropFiles, acceptedFiles, _rejectedFiles) => setFile((file) => acceptedFiles[0]), []);

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

  const fileUpload = !file && <DropZone.FileUpload actionTitle='Add photo' actionHint='or drop an file to upload' />;

  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size='small'
        alt={file.name}
        source={
          validImageTypes.indexOf(file.type) > 0
            ? window.URL.createObjectURL(file)
            : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
        }
      />
      <div>
        {file.name} <Caption>{file.size} bytes</Caption>
      </div>
    </Stack>
  );

  const renderPreviewPhoto = () => {
    if (!!file) return window.URL.createObjectURL(file);
    if (selectedTestimonial?.testimonialPhoto?.name) {
      return window.URL.createObjectURL(selectedTestimonial.testimonialPhoto);
    }
    if (selectedTestimonial?.testimonialPhoto) return selectedTestimonial?.testimonialPhoto;
    return uploadImageUrl;
  };

  const handleRemoveBackgroundImage = () => {
    setFile(null);
  };

  useEffect(() => {
    const careerPortal = propOr({}, 'careerPortal')(data);
    const styleControls = propOr({}, 'styleControls')(careerPortal);
    const testimonialSection = propOr({}, 'testimonial_section')(styleControls);
    const testimonials = propOr([], 'testimonials')(careerPortal);
    const styleControlsFormatted = keysToCamel(careerPortal.styleControls);
    const talentCommunitySection = propOr(false, 'talentCommunitySection')(careerPortal);
    const employeeTestimonial = propOr(false, 'employeeTestimonial')(careerPortal);
    setCareerPortalTime(careerPortal.pageTimestamps);
    setForm((prevForm) => ({
      ...prevForm,
      testimonials,
      ...styleControlsFormatted,
      employeeTestimonial,
      testimonialSection: {
        ...prevForm.testimonialSection,
        headingColor: testimonialSection?.heading_color || defaultDarkGrayColor,
        headingWeight: testimonialSection?.heading_weight || '700',
        headingSize: testimonialSection?.heading_size || '26',
        headingFontFamily: testimonialSection?.heading_font_family || 'inherit',
        headingTextCasing: testimonialSection?.heading_text_casing || 'uppercase',
        backgroundColor: testimonialSection?.background_color || defaultWhiteColor,
        employeeColor: testimonialSection?.employee_color || defaultDarkGrayColor,
        subheadingFontFamily: testimonialSection?.subheading_font_family || 'inherit',
        quoteColor: testimonialSection?.quote_color || defaultDarkGrayColor,
        bodyFontFamily: testimonialSection?.body_font_family || 'inherit',
        topSpacing: testimonialSection?.top_spacing || '80',
        bottomSpacing: testimonialSection?.bottom_spacing || '80',
      },
      employeeTestimonialTitle: careerPortal?.employeeTestimonialTitle || '',
      talentCommunityStyles: {
        ...styleControlsFormatted?.talentCommunitySection,
      },
      talentCommunitySection,
    }));
  }, [data]);

  if (loading || isLoading) {
    return (
      <div className='loader-container'>
        <Loader type='TailSpin' color='#6371c7' height={80} width={80} />
      </div>
    );
  }

  const filterPageTimeLabel = (label) => {
    if (!careerPortalTime) return;
    return careerPortalTime.filter((val) => val.attribute == label)[0].label;
  };

  const updateField = (fieldName) => (value) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      [fieldName]: value,
    });
  };

  const updateColor = (fieldName) => (color) => {
    setIsSaveBarActive(true);
    setForm({
      ...form,
      testimonialSection: {
        ...form.testimonialSection,
        [fieldName]: color,
      },
    });
  };

  const updateFontValue =
    ({ sectionName, fieldName }) =>
    (value) => {
      setIsSaveBarActive(true);
      setForm({
        ...form,
        [sectionName]: {
          ...form[sectionName],
          [fieldName]: `${value}`,
        },
      });
    };

  const toggleToast = (text) =>
    setToast((prev) => ({
      isOpen: !prev.isOpen,
      text: prev.isOpen ? prev.text : text,
    }));

  const onSuccess = () => {
    refetch();
    setTimeout(() => {
      setIsSaveBarActive(false);
      setShowSuccessBanner(true);
      setIsLoading(false);
    }, 1000);
  };

  const onError = (e) => {
    let messages = path(['graphQLErrors', 0, 'message'])(e);
    toggleToast(`Error: ${messages}`);
    setIsLoading(false);
  };

  const onSubmit =
    (publish = true) =>
    (e) => {
      e?.preventDefault();
      setPreview(false);
      setConfirmModal(false);
      let promisesArray = [];

      if (!isEmpty(form.testimonials)) {
        promisesArray = form.testimonials.map(({ isNew, id, ...variables }) => {
          if (isNew) {
            return createTestimonial({ variables: { ...variables, shopifyToken } });
          }
          const updated = {
            testimonialId: id,
            ...variables,
            testimonialPhoto: variables.testimonialPhoto?.name ? variables.testimonialPhoto : null,
            shopifyToken,
          };
          return updateTestimonial({
            variables: updated,
          });
        });
      }

      setIsLoading(true);
      const careerPortal = propOr({}, 'careerPortal')(data);
      const save = isCareerPortalEmpty ? createShopCareerPortal : updateShopCareerPortal;
      const payload = { variables: { ...careerPortal, ...form, shopifyToken, employeeTestimonial: publish } };

      delete payload.variables.styleControls;

      Promise.all([...promisesArray, save(payload)])
        .then(onSuccess)
        .catch(onError);
    };

  const toastMarkup = toastStatus.isOpen ? <Toast content={toastStatus.text} onDismiss={toggleToast} /> : null;

  return (
    <AppProvider
      i18n={{
        Polaris: {
          DropZone: {
            actionTitle: 'Add image',
          },
        },
      }}>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'Career Portal', onAction: () => { history.push('/career-portal-index'); } }]}
          title='Employee testimonials section'
          subtitle={filterPageTimeLabel('employee_testimonial')}
          titleMetadata={
            <Badge status={form.employeeTestimonial ? 'success' : 'default'}>
              {form.employeeTestimonial ? 'Published' : 'Unpublished'}
            </Badge>
          }
          pagination={{
            hasPrevious: !isSaveBarActive,
            hasNext: !isSaveBarActive,
            nextURL: !isSaveBarActive ? `/career-portal/diversity-statement-section` : null,
            previousURL: !isSaveBarActive ? `/career-portal/intro-paragraph-section` : null,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <ButtonGroup>
              <Button plain monochrome onClick={togglePreviewModal}>
                Preview
              </Button>
              <Button
                primary
                onClick={onSubmit(true)}
                disabled={!isSaveBarActive && form.employeeTestimonial}
                connectedDisclosure={
                  form.employeeTestimonial
                    ? {
                        accessibilityLabel: 'Change section status',
                        actions: [{ content: 'Unpublish', onAction: toggleConfirmModal }],
                      }
                    : undefined
                }>
                Publish
              </Button>
            </ButtonGroup>
          }>
          <Form onSubmit={onSubmit()}>
            {showSuccessBanner && (
              <SuccessBanner
                history={history}
                title='Employee testimonials section updated'
                onDismiss={() => setShowSuccessBanner(false)}
                portalBody={'View on your career portal'}
                detailsBody={'go to all career portal sections'}
                portalUrl={careerPageLink}
                detailsUrl={'/career-portal-index'}
              />
            )}
            <Layout>
              <Layout.AnnotatedSection
                title='Content'
                description={
                  <>
                    <p style={{ marginBottom: '10px' }}>
                      Add social proof using employee testimonials to help communicate your employer brand to job seekers.
                    </p>
                  </>
                }>
                <Card sectioned>
                  <TextField
                    id='employeeTestimonialTitle'
                    label='Title text'
                    value={form.employeeTestimonialTitle}
                    name='employeeTestimonialTitle'
                    onChange={updateField('employeeTestimonialTitle')}
                  />
                  <br />
                  <TextStyle>Testimonials</TextStyle>
                  <ResourceList
                    items={form.testimonials}
                    renderItem={(item) => {
                      const { id, testimonialPhoto, name, role, quote } = item;
                      return (
                        <ResourceItem
                          id={id}
                          shortcutActions={[
                            {
                              content: 'Edit',
                              onAction: () => {
                                setSelectedTestimonial(item);
                                setForm({
                                  ...form,
                                  employeeTestimonialName: item.name,
                                  employeeTestimonialQuote: item.quote,
                                  employeeTestimonialRole: item.role,
                                });
                                toggleIsTestimonialModalOpen();
                              },
                            },
                            {
                              content: 'Remove',
                              onAction: () => {
                                toggleIsConfirmDeleteModalOpen();
                                setSelectedTestimonial(item);
                              },
                            },
                          ]}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <img
                              style={{ maxWidth: '70px', marginRight: '15px' }}
                              src={testimonialPhoto?.name ? window.URL.createObjectURL(testimonialPhoto) : testimonialPhoto}
                              alt='Employee Photo'
                            />
                            <div style={{ maxWidth: '300px' }}>
                              <h3>
                                <TextStyle variation='strong'>{name}</TextStyle>
                              </h3>
                              <div>{role}</div>
                              <div>{quote}</div>
                            </div>
                          </div>
                        </ResourceItem>
                      );
                    }}
                  />

                  <div className={'left-hand-footer'}>
                    <Button onClick={toggleIsTestimonialModalOpen}>Add testimonial</Button>
                  </div>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Layout'
                description='Customize the content alignment and spacing to match your store’s theme.'>
                <Card sectioned>
                  <FormLayout>
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Top spacing'
                        value={form.testimonialSection?.topSpacing}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'topSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.testimonialSection?.topSpacing}px`}
                      />
                      <RangeSlider
                        label='Bottom spacing'
                        value={form.testimonialSection?.bottomSpacing}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'bottomSpacing' })}
                        output
                        max={300}
                        min={10}
                        suffix={`${form.testimonialSection?.bottomSpacing}px`}
                      />
                    </div>
                  </FormLayout>
                </Card>
              </Layout.AnnotatedSection>
              <Layout.AnnotatedSection
                title='Appearance'
                description='Customize the font and section background colors to match your store’s theme.'>
                <Card sectioned>
                  <Card.Section title='SECTION'>
                    <ColorPicker
                      colorName='Background color'
                      defaultColor={form.testimonialSection?.backgroundColor}
                      onSelectColor={updateColor('backgroundColor')}
                    />
                  </Card.Section>
                  <Card.Section title='TITLE TEXT'>
                    <ColorPicker
                      colorName='Title color'
                      defaultColor={form.testimonialSection?.headingColor}
                      onSelectColor={updateColor('headingColor')}
                    />
                    <div style={{ width: '60%' }}>
                      <RangeSlider
                        label='Title font size'
                        value={form.testimonialSection?.headingSize}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'headingSize' })}
                        output
                        max={72}
                        min={12}
                        suffix={`${form.testimonialSection?.headingSize}px`}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'headingFontFamily' })}
                        value={form.testimonialSection?.headingFontFamily}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title font weight'
                        options={FONT_WEIGHT_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'headingWeight' })}
                        value={form.testimonialSection?.headingWeight}
                      />
                    </div>
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Title text casing'
                        options={FONT_CASING_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'headingTextCasing' })}
                        value={form.testimonialSection?.headingTextCasing}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='QUOTE TEXT'>
                    <ColorPicker
                      colorName='Quote color'
                      defaultColor={form.testimonialSection?.quoteColor}
                      onSelectColor={updateColor('quoteColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Quote font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'bodyFontFamily' })}
                        value={form.testimonialSection?.bodyFontFamily}
                      />
                    </div>
                  </Card.Section>
                  <Card.Section title='EMPLOYEE NAME TEXT'>
                    <ColorPicker
                      colorName='Employee name'
                      defaultColor={form.testimonialSection?.employeeColor}
                      onSelectColor={updateColor('employeeColor')}
                    />
                    <div style={{ width: '50%' }}>
                      <Select
                        label='Employee name font family'
                        options={FONT_FAMILY_OPTIONS}
                        onChange={updateFontValue({ sectionName: 'testimonialSection', fieldName: 'subheadingFontFamily' })}
                        value={form.testimonialSection?.subheadingFontFamily}
                      />
                    </div>
                  </Card.Section>
                </Card>
              </Layout.AnnotatedSection>
            </Layout>
            <div className='career-portal__form-buttons'>
              <Button onClick={() => changeRoute('/career-portal-index')}>Cancel</Button>
              <Button primary submit disabled={!isSaveBarActive && form.employeeTestimonial}>
                Publish
              </Button>
            </div>
          </Form>
        </Page>
        {toastMarkup}
      </Frame>
      <Modal
        open={isTestimonialModalOpen}
        onClose={toggleIsTestimonialModalOpen}
        title={selectedTestimonial?.id ? 'Update testimonial' : 'Add testimonial'}
        primaryAction={{
          content: selectedTestimonial?.id ? 'Update testimonial' : 'Add testimonial',
          onAction: () => {
            const updatedTestimonial = {
              name: form.employeeTestimonialName,
              quote: form.employeeTestimonialQuote,
              role: form.employeeTestimonialRole,
              testimonialPhoto: file || null,
              shopifyToken,
            };

            let updatedTestimonials = [...form.testimonials];
            const newTestimonialId = `new-${updatedTestimonials.length + 1}-${updatedTestimonial.name}`;
            if (!selectedTestimonial?.id) {
              updatedTestimonials.push({ id: newTestimonialId, ...updatedTestimonial, isNew: true });
            } else {
              updatedTestimonials = updatedTestimonials.map((testimonial) => {
                if (testimonial.id === selectedTestimonial?.id)
                  return {
                    ...testimonial,
                    ...updatedTestimonial,
                    testimonialPhoto: updatedTestimonial.testimonialPhoto || testimonial.testimonialPhoto,
                  };
                return testimonial;
              });
            }

            setForm({
              ...form,
              employeeTestimonialName: '',
              employeeTestimonialQuote: '',
              employeeTestimonialRole: '',
              testimonials: updatedTestimonials,
            });
            setFile(null);
            toggleIsTestimonialModalOpen();
            setSelectedTestimonial(null);
          },
          disabled:
            (!file && !selectedTestimonial?.id) ||
            !form.employeeTestimonialName ||
            !form.employeeTestimonialQuote ||
            !form.employeeTestimonialRole ||
            isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setForm({
                ...form,
                employeeTestimonialName: '',
                employeeTestimonialQuote: '',
                employeeTestimonialRole: '',
              });
              setSelectedTestimonial(null);
              toggleIsTestimonialModalOpen();
            },
          },
        ]}
        loading={false}>
        <Modal.Section>
          <FormLayout>
            <FormLayout.Group>
              <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                {uploadedFile}
                {fileUpload}
              </DropZone>
              <div>
                <Subheading>Preview</Subheading>
                <br />
                <Card>
                  <Card.Section>
                    <img style={{ width: '70px' }} src={renderPreviewPhoto()} />
                  </Card.Section>
                </Card>
                <br />
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button primary disabled={!file} onClick={handleRemoveBackgroundImage}>
                    Remove
                  </Button>
                </div>
              </div>
            </FormLayout.Group>
          </FormLayout>
          <br />
          <FormLayout>
            <TextField
              id='employeeTestimonialName'
              label='Employee name'
              value={form.employeeTestimonialName}
              name='employeeTestimonialName'
              onChange={updateField('employeeTestimonialName')}
            />
            <TextField
              id='employeeTestimonialRole'
              label='Job title'
              value={form.employeeTestimonialRole}
              name='employeeTestimonialRole'
              onChange={updateField('employeeTestimonialRole')}
            />
            <TextField
              id='employeeTestimonialQuote'
              label='Quote'
              value={form.employeeTestimonialQuote}
              name='employeeTestimonialQuote'
              multiline={5}
              onChange={updateField('employeeTestimonialQuote')}
            />
          </FormLayout>
        </Modal.Section>
      </Modal>
      <Modal
        open={isConfirmDeleteModalOpen}
        onClose={toggleIsConfirmDeleteModalOpen}
        title='Remove testimonial'
        primaryAction={{
          content: 'Delete',
          onAction: () => {
            if (selectedTestimonial.isNew) {
              setForm({
                ...form,
                testimonials: form.testimonials.filter(({ id }) => id !== selectedTestimonial.id),
              });
              setSelectedTestimonial(null);
              toggleIsConfirmDeleteModalOpen();
            } else {
              setIsLoading(true);
              deleteTestimonial({
                variables: {
                  shopifyToken,
                  testimonialId: selectedTestimonial.id,
                },
              })
                .then(() => {
                  setForm({
                    ...form,
                    testimonials: form.testimonials.filter(({ id }) => id !== selectedTestimonial.id),
                  });
                  toggleIsConfirmDeleteModalOpen();
                  toggleToast('The testimonial was deleted successfully!');
                  setIsLoading(false);
                  setSelectedTestimonial(null);
                  refetch();
                })
                .catch(() => {
                  setIsLoading(false);
                  toggleToast('There was a problem with your request');
                });
            }
          },
          disabled: isLoading,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleIsConfirmDeleteModalOpen,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>Are you sure you want to delete this testimonial?</p>
        </Modal.Section>
      </Modal>
      <Modal
        open={!!preview}
        large
        onClose={togglePreviewModal}
        primaryAction={{
          content: 'Publish',
          disabled: !isSaveBarActive && form.employeeTestimonial,
          onAction: () => {
            onSubmit();
          },
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: togglePreviewModal,
          },
        ]}
        title={
          <TextContainer>
            <Heading>Preview employee testimonials section</Heading>
            <p style={{ fontSize: '0.85em' }}>
              Your theme fonts are not applied in the preview bellow, publish the section to your career portal to see how the
              section will look on your store
            </p>
          </TextContainer>
        }
        loading={false}>
        <div className={'preview__modal'}>
          <section
            className='testimonial_section'
            style={{
              color: `${hslaColor(form.testimonialSection?.backgroundColor, '#fff')}`,
            }}>
            <div className='wrapper'>
              <h2
                className='heading_text'
                style={{
                  color: `${hslaColor(form.testimonialSection?.headingColor, '#3a3a3a')}`,
                  fontSize: `${form.testimonialSection?.headingSize || '26'}px`,
                  fontWeight: `${form.testimonialSection?.headingWeight || '700'}`,
                }}>
                {form.employeeTestimonialTitle || 'Employee Testimonials'}
              </h2>
              {(form.testimonials || []).map((item, index) => {
                const { id, testimonialPhoto, name, role, quote } = item;
                return (
                  <div className='testimonial_item' key={id}>
                    {index % 2 === 0 && (
                      <div className='testimonial_item_image left'>
                        <img
                          src={testimonialPhoto?.name ? window.URL.createObjectURL(testimonialPhoto) : testimonialPhoto}
                          alt=''
                          role='presentation'
                        />
                      </div>
                    )}
                    <div className='testimonial_item_content'>
                      <span
                        className='quote'
                        style={{
                          color: `${hslaColor(form.testimonialSection?.quoteColor, '#3a3a3a')}`,
                        }}>
                        “
                      </span>
                      <p
                        className='testimonial_body'
                        style={{
                          color: `${hslaColor(form.testimonialSection?.quoteColor, '#3a3a3a')}`,
                        }}>
                        {quote}
                      </p>
                      <p
                        className='testimonial_employee'
                        style={{
                          color: `${hslaColor(form.testimonialSection?.employeeColor, '#3a3a3a')}`,
                        }}>
                        {name}
                      </p>
                      <p
                        className='testimonial_role'
                        style={{
                          color: `${hslaColor(form.testimonialSection?.jobTitleColor, '#3a3a3a')}`,
                        }}>
                        {role}
                      </p>
                    </div>
                    {index % 2 !== 0 && (
                      <div className='testimonial_item_image right'>
                        <img
                          src={testimonialPhoto?.name ? window.URL.createObjectURL(testimonialPhoto) : testimonialPhoto}
                          alt=''
                          role='presentation'
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </Modal>
      <Modal
        open={confirmModal}
        onClose={toggleConfirmModal}
        title={`${form.employeeTestimonial ? 'Unpublish' : 'Publish'} section`}
        primaryAction={{
          content: `Yes, ${form.employeeTestimonial ? 'unpublish' : 'publish'} section`,
          onAction: onSubmit(!form.employeeTestimonial),
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: toggleConfirmModal,
          },
        ]}
        loading={false}>
        <Modal.Section>
          <p>{`Do you want to ${form.employeeTestimonial ? 'unpublish' : 'publish'} this section to your career portal page?`}</p>
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
};

export default EmployeeTestimonialsSection;
